/**
 * CitationList — renders Phase 4 citations as accessible semantic HTML.
 *
 * Design D-CITE: citation UI is structurally distinguishable from agent opinion.
 * Renders citations as an ordered list (<ol>) with semantic <li> items.
 *
 * Accessibility (spec §4.3, CLAUDE.md §7):
 *   - Semantic <ol>/<li> elements — not images or color-only signals.
 *   - Each link has an aria-label for screen readers.
 *   - Links are keyboard-reachable (standard <a> in tab order).
 *   - rel="noopener noreferrer" and target="_blank" on external links.
 *
 * Security:
 *   - textContent only — title, snippet, and url are rendered as React children
 *     (never via dangerouslySetInnerHTML).
 *   - XSS: React escapes all string content automatically.
 *
 * Returns null when citations array is empty (no DOM output).
 */

import React from "react";

/** Citation shape — mirrors worker/src/knowledge/types.ts Citation interface. */
export interface Citation {
  /** Discriminant: "knowledge" (RAG) or "web" (Tavily). */
  source: "knowledge" | "web";
  /** Human-readable title of the source document or article. */
  title: string;
  /** URL — present for web citations and ingested docs with a source URL. */
  url?: string;
  /** Firestore document ID — present for knowledge base chunks. */
  docId?: string;
  /** Short excerpt shown to the user. */
  snippet: string;
}

export interface CitationListProps {
  citations: Citation[];
}

/**
 * CitationList renders a labelled ordered list of citations.
 * Returns null when citations is empty — no DOM output in that case.
 */
export function CitationList({ citations }: CitationListProps): JSX.Element | null {
  if (citations.length === 0) return null;

  return (
    <section
      aria-label="Fuentes citadas"
      className="mt-2 rounded-xl border border-zinc-700 bg-zinc-900/60 px-4 py-3 text-xs"
    >
      <p className="mb-2 font-semibold text-zinc-400 uppercase tracking-wide text-[10px]">
        Fuentes
      </p>
      <ol className="flex flex-col gap-2 list-decimal list-inside">
        {citations.map((citation, index) => (
          <CitationItem key={`${citation.source}-${citation.docId ?? citation.url ?? index}`} citation={citation} index={index} />
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CitationItem — single citation entry
// ---------------------------------------------------------------------------

interface CitationItemProps {
  citation: Citation;
  index: number;
}

function CitationItem({ citation, index }: CitationItemProps): JSX.Element {
  const label = `Fuente ${index + 1}: ${citation.title}`;

  return (
    <li className="text-zinc-400 leading-snug">
      {citation.url != null && citation.url.length > 0 ? (
        // Web citation: render as accessible link
        <a
          href={citation.url}
          rel="noopener noreferrer"
          target="_blank"
          aria-label={label}
          className="inline font-medium text-blue-400 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
        >
          {/* textContent only — React renders text nodes, never innerHTML */}
          {citation.title}
        </a>
      ) : (
        // Knowledge base citation: no external URL, render as text
        <span className="font-medium text-zinc-300">{citation.title}</span>
      )}
      {citation.snippet.length > 0 ? (
        <span className="ml-1 text-zinc-500">
          {" — "}
          {citation.snippet}
        </span>
      ) : null}
    </li>
  );
}
